import { useAbpPermissions } from '../contexts/AbpPermissionsContext';

/**
 * Tenant branding settings for white labeling
 */
export interface TenantBranding {
  /**
   * Tenant name (replaces "LearnLeaf" if not empty)
   */
  name: string;

  /**
   * Tenant logo URL (replaces default full logo if not empty)
   */
  logoUrl: string;

  /**
   * Tenant icon URL (replaces default icon logo if not empty)
   */
  iconUrl: string;

  /**
   * Tenant theme CSS (injected into DOM if not empty)
   */
  themeCSS: string;

  /**
   * Whether tenant has custom branding configured
   */
  hasCustomBranding: boolean;
}

/**
 * Hook to access tenant branding settings for white labeling.
 *
 * Reads settings from ABP configuration:
 * - learnleaf.AppSettings.TenantName
 * - learnleaf.AppSettings.TenantLogoUrl
 * - learnleaf.AppSettings.TenantIconUrl
 * - learnleaf.AppSettings.TenantThemeCSS
 *
 * @example
 * ```tsx
 * const { name, logoUrl, iconUrl, hasCustomBranding } = useTenantBranding();
 *
 * return <h1>{name || 'LearnLeaf'}</h1>;
 * ```
 */
export function useTenantBranding(): TenantBranding {
  const { getSetting } = useAbpPermissions();

  const name = getSetting('learnleaf.AppSettings.TenantName', '');
  const logoUrl = getSetting('learnleaf.AppSettings.TenantLogoUrl', '');
  const iconUrl = getSetting('learnleaf.AppSettings.TenantIconUrl', '');
  const themeCSS = getSetting('learnleaf.AppSettings.TenantThemeCSS', '');

  const hasCustomBranding = !!(name || logoUrl || iconUrl || themeCSS);

  return {
    name,
    logoUrl,
    iconUrl,
    themeCSS,
    hasCustomBranding,
  };
}

/**
 * Helper to get the application name (tenant name or default "LearnLeaf")
 */
export function useAppName(): string {
  const { name } = useTenantBranding();
  return name || 'LearnLeaf';
}

/**
 * Helper to get the application logo URL (tenant logo or null for default)
 */
export function useAppLogoUrl(): string | null {
  const { logoUrl } = useTenantBranding();
  return logoUrl || null;
}

/**
 * Helper to get the application icon URL (tenant icon or null for default)
 */
export function useAppIconUrl(): string | null {
  const { iconUrl } = useTenantBranding();
  return iconUrl || null;
}
